import React from "react";

type Variant = "info" | "success" | "warning" | "danger";

interface InlineAlertProps {
  message: string;
  variant?: Variant; // default "info"
  style?: React.CSSProperties;
}

const THEMES: Record<
  Variant,
  { border: string; iconBg: string; icon: React.ReactNode }
> = {
  info: {
    border: "#06B6D4",
    iconBg: "#DBF0F6",
    icon: (
      <svg
        width={18}
        height={18}
        viewBox="0 0 32 32"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M16 1c8.271 0 15 6.729 15 15s-6.729 15-15 15S1 24.271 1 16 7.729 1 16 1zm0 2C8.832 3 3 8.832 3 16s5.832 13 13 13 13-5.832 13-13S23.168 3 16 3zm-.933 10.133c1.066 0 1.933.867 1.933 1.933v4.667c0 .478.389.867.866.867h.934a1 1 0 110 2h-.934A2.87 2.87 0 0115 19.733v-4.667l-.867.067a1 1 0 110-2zm.466-4.666c.81 0 1.467.658 1.467 1.466 0 .809-.658 1.467-1.467 1.467a1.469 1.469 0 01-1.467-1.467c0-.808.658-1.466 1.467-1.466z"
          fill="#06B6D4"
          fillRule="evenodd"
        />
      </svg>
    ),
  },
  success: {
    border: "#059669",
    iconBg: "#D5EADF",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17.2179 8.30483C17.6024 8.70132 17.5926 9.33441 17.1961 9.71888L11.0086 15.7189C10.6208 16.095 10.0042 16.095 9.61635 15.7189L6.80385 12.9916C6.40737 12.6071 6.39763 11.974 6.7821 11.5776C7.16657 11.1811 7.79966 11.1713 8.19615 11.5558L10.3125 13.608L15.8039 8.28308C16.2003 7.89861 16.8334 7.90835 17.2179 8.30483Z"
          fill="#059669"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12 3.00098C7.02944 3.00098 3 7.03041 3 12.001C3 16.9715 7.02944 21.001 12 21.001C16.9706 21.001 21 16.9715 21 12.001C21 7.03041 16.9706 3.00098 12 3.00098ZM1 12.001C1 5.92584 5.92487 1.00098 12 1.00098C18.0751 1.00098 23 5.92584 23 12.001C23 18.0761 18.0751 23.001 12 23.001C5.92487 23.001 1 18.0761 1 12.001Z"
          fill="#059669"
        />
      </svg>
    ),
  },
  danger: {
    border: "#D64541",
    iconBg: "#FDDBD6",
    icon: (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="21" viewBox="0 0 24 21" fill="none">
      <path fillRule="evenodd" clipRule="evenodd" d="M12 6.99998C12.5523 6.99998 13 7.4477 13 7.99998V12C13 12.5523 12.5523 13 12 13C11.4477 13 11 12.5523 11 12V7.99998C11 7.4477 11.4477 6.99998 12 6.99998Z" fill="#D64541" />
      <path d="M13 16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16C11 15.4477 11.4477 15 12 15C12.5523 15 13 15.4477 13 16Z" fill="#D64541" />
      <path fillRule="evenodd" clipRule="evenodd" d="M10.2304 1.49653C10.9986 0.164738 12.9197 0.16168 13.6921 1.49103L22.9916 17.4952C23.7664 18.8285 22.8044 20.5 21.2624 20.5H2.73125C1.192 20.5 0.229707 18.834 0.998809 17.5007L10.2304 1.49653ZM21.2624 18.5L11.9629 2.49585L2.73125 18.5H21.2624Z" fill="#D64541" />
    </svg>)
  },
  warning: {
    border: "#F7931E",
    iconBg: "#FFE9D3",
    icon: (<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M12 7C12 6.44772 11.5523 6 11 6C10.4477 6 10 6.44772 10 7V11C10 11.5523 10.4477 12 11 12C11.5523 12 12 11.5523 12 11V7Z" fill="#F7931E" />
      <path fillRule="evenodd" clipRule="evenodd" d="M11 0C4.92487 0 0 4.92487 0 11C0 17.0751 4.92487 22 11 22C17.0751 22 22 17.0751 22 11C22 4.92487 17.0751 0 11 0ZM2 11C2 6.02944 6.02944 2 11 2C15.9706 2 20 6.02944 20 11C20 15.9706 15.9706 20 11 20C6.02944 20 2 15.9706 2 11Z" fill="#F7931E" />
      <path d="M12 15C12 15.5523 11.5523 16 11 16C10.4477 16 10 15.5523 10 15C10 14.4477 10.4477 14 11 14C11.5523 14 12 14.4477 12 15Z" fill="#F7931E" />
    </svg>)
  }
};

const InlineAlert: React.FC<InlineAlertProps> = ({
  message,
  variant = "info",
  style,
}) => {
  const { border, iconBg, icon } = THEMES[variant];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        ...style,
      }}
      role="status"
    >
      <div
        style={{
          display: "flex",
          padding: 16,
          alignItems: "center",
          borderRadius: "4px 0 0 4px",
          border: `1px solid ${border}`,
          background: iconBg, // tinted chip behind icon
        }}
      >
        {icon}
      </div>

      <div
        style={{
          display: "flex",
          padding: 16,
          flexDirection: "column",
          alignItems: "flex-start",
          flex: "1 0 0",
          borderRadius: "0 4px 4px 0",
          border: `1px solid ${border}`,
          borderLeft: "none",
          background: "#FFF",
          color: "#667085",
        }}
      >
        <p style={{ marginTop: 0, marginBottom: 0 }}
          dangerouslySetInnerHTML={{ __html: message }} />
      </div>
    </div>
  );
};

export default InlineAlert;